import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "../../shared/api/index";
import { useNotification } from "~hooks/useNotification";

export const useFetchConference = () => {
  const { fetchData } = useApi();
  const { sport, organizationId, dataId } = useParams();
  const notification = useNotification();

  return useQuery({
    queryKey: ["conferences", organizationId, dataId],
    queryFn: async () => {
      const result = await fetchData({
        url: `${sport}/o/${organizationId}/conferences/${dataId}`,
      });
      return result?.data?.[0];
    },
    enabled: !!dataId,
  });
};

export const useFetchConferences = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport } = useParams();
  let URL = `${sport}/o/${organizationId}/conferences`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }
  const notification = useNotification();

  return useQuery({
    queryKey: ["conferences", organizationId, JSON.stringify(params)],
    queryFn: async () => {
      return await fetchData({
        url: URL,
      });
    },
  });
};

export const useDeleteConference = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (conferenceId) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/conferences/${conferenceId}`,
        method: "DELETE",
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "conference.delete.success",
          defaultMessage: "Conference deleted successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["conferences"] });
    },
  });
};

export const useCreateConference = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (data) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/conferences`,
        method: "POST",
        data,
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "conferences.create.success",
          defaultMessage: "Conferences created successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["conferences"] });
    },
  });
};

export const useUpdateConference = () => {
  const { sport, organizationId, dataId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (data) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/conferences/${dataId}`,
        method: "PUT",
        data,
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "conferences.update.success",
          defaultMessage: "Сonference updated successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["conferences"] });
    },
  });
};
